import type { IWork, IWorkTitleSummary } from "containers/Works/types";
import type { ITitle } from "containers/Titles/types";

export type TitleTag = { id: number; name: string; color: number };

export const normalizeTitleTags = (tags: unknown): TitleTag[] => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags as TitleTag[];
    if (typeof tags === "string") {
        try {
            const parsed: unknown = JSON.parse(tags);
            return Array.isArray(parsed) ? (parsed as TitleTag[]) : [];
        } catch {
            return [];
        }
    }
    return [];
};

export const buildTagFromPostPayload = (payload: { tagId: number; name: string; color: number }): TitleTag => ({
    id: Number(payload.tagId),
    name: payload.name,
    color: Number(payload.color),
});

export const appendTitleTag = (tags: unknown, tag: TitleTag): TitleTag[] => {
    const current = normalizeTitleTags(tags);
    if (current.some((item) => item.id === tag.id)) return current;
    return [...current, tag];
};

export const removeTitleTag = (tags: unknown, tagId: number): TitleTag[] =>
    normalizeTitleTags(tags).filter((tag) => tag.id !== tagId);

export const patchTitleInList = (titles: ITitle[], titleId: number, patch: (title: ITitle) => ITitle): ITitle[] => {
    const index = titles.findIndex((title) => title.id === titleId);
    if (index === -1) return titles;
    const next = [...titles];
    next[index] = patch(next[index]);
    return next;
};

export const patchWorkTitleTags = (
    titles: IWorkTitleSummary[],
    titleId: number,
    patchTags: (tags: unknown) => TitleTag[],
): IWorkTitleSummary[] => {
    const index = titles.findIndex((title) => title.id === titleId);
    if (index === -1) return titles;
    const next = [...titles];
    next[index] = {
        ...next[index],
        tags: patchTags(next[index].tags),
    };
    return next;
};

/** Propagate title tag changes into a work's embedded title summaries. */
export const patchTitleTagsInWork = (
    work: IWork,
    titleId: number,
    patchTags: (tags: unknown) => TitleTag[],
): IWork => ({
    ...work,
    titles: patchWorkTitleTags(work.titles, titleId, patchTags),
});
